import {
  EUrlParams,
  RecordLimit,
  EAction,
  EActionType,
  EHttpCode,
  ERROR_MESSAGE_KEYS,
} from '../data/Constants';
import { setError } from '../store/states/ErrorSlice';
import { setAlertMessage } from '../store/states/AlertMessageSlice';
import { resetDialogMessage } from '../store/states/DialogSlice';
import { t } from 'i18next';
import type { IError } from '../../types';
import type { Dispatch } from '@reduxjs/toolkit';

export function handleSuccess(response, action?: string) {
  if (action === EAction.Download) {
    const fileName = response.headers['content-disposition'].split('filename=')[1].trim();
    const url = globalThis.URL.createObjectURL(
      new Blob([response.data], {
        type: response.headers['content-type'] || 'application/zip',
      })
    );
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
  } else {
    return {
      data: response.data ? response.data : [],
    };
  }
}

/**
 * Handle API errors by dispatching to Redux store and showing alerts
 *
 * @param dispatch - Redux dispatch function
 * @param err - The error object from axios
 * @param action - The action context (Read, Create, Get, Refresh, etc.)
 * @param apiType - The API type for error tracking
 * @returns Error object for Redux state
 */
export function handleError(dispatch: Dispatch, err: IError, action?: string, apiType?: string) {
  const errObj = {
    type: apiType ?? '',
    code: err.response?.status,
    action: action ?? '',
    message:
      err.response?.data?.message ||
      ((err.response?.data as any)?.errors
        ? (Object.values((err.response.data as any).errors)[0] as any)[0]
        : ''),
  };
  dispatch(setError(errObj));

  if (action && action in ERROR_MESSAGE_KEYS) {
    const message =
      errObj.message || t(ERROR_MESSAGE_KEYS[action as keyof typeof ERROR_MESSAGE_KEYS]);
    dispatch(setAlertMessage({ show: true, message, type: 'error' }));

    if (action === EAction.Get) {
      dispatch(resetDialogMessage());
    }
  }

  return {
    error: errObj,
  };
}

export function getDefaultPagination(type?: keyof typeof RecordLimit) {
  const limit = type && RecordLimit[type] ? RecordLimit[type] : RecordLimit.default;
  return `${EUrlParams.Page}=1&${EUrlParams.Limit}=` + limit;
}

/**
 * Handle API errors with graceful degradation for 404/400 errors
 * Returns empty data for not found errors, otherwise dispatches error handling
 *
 * @param dispatch - Redux dispatch function
 * @param err - The error object from axios
 * @param action - Action context object
 * @param action.page - Current page number
 * @param action.searchKey - The search key to return in empty response (searchCode, productId, materialCode, etc.)
 * @param action.caller - The caller context (e.g., Refresh)
 * @param action.skipUpdate - Whether to skip updating the store
 * @param apiAction - The EAction type (Read, Create, etc.)
 * @param apiType - The EApiType for error tracking
 * @param returnEmptyOn - HTTP status codes that should return empty data (default: [404, 400])
 * @returns Empty data structure or error object
 */
export function handleApiErrorWithFallback(
  dispatch: Dispatch,
  err: IError,
  action: { page: number; searchKey?: string; caller?: string; skipUpdate?: boolean },
  apiAction?: string,
  apiType?: string,
  returnEmptyOn: number[] = [EHttpCode.Status404, EHttpCode.Status400]
) {
  const statusCode = err.response?.status;

  // Return empty data for specified status codes (graceful degradation)
  if (statusCode && returnEmptyOn.includes(statusCode)) {
    if (action.caller === EActionType.Refresh) {
      return handleError(dispatch, err, EActionType.Refresh);
    }
    return {
      data: [],
      searchKey: action.searchKey || '',
      page: action.page,
      totalRecords: 1,
      caller: action.caller,
      skipUpdate: action.skipUpdate,
    };
  }

  if (action.skipUpdate) {
    return handleError(dispatch, err, EAction.Get);
  }
  // Handle as normal error
  return handleError(dispatch, err, apiAction, apiType);
}
